import { useEffect, useRef, useState } from "react";

const BINANCE_API = "https://api.binance.com/api/v3/ticker/24hr";
const BINANCE_WS = "wss://stream.binance.com:9443/ws/!ticker@arr";

const COINGECKO_API = "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&per_page=250&page=1";

const useBinanceAllTickers = () => {
    const [tickers, setTickers] = useState([]);
    const [loading, setLoading] = useState(true);

    const socketRef = useRef(null);
    const reconnectRef = useRef(null);
    const mountedRef = useRef(false);

    // market cap cache
    const marketCapRef = useRef({});
        
    useEffect(() => {
        mountedRef.current = true;

        /* ================= SYMBOL HELPERS ================= */
        const toCGSymbol = (symbol) => symbol?.replace("USDT", "").toUpperCase();

        /* ================= FETCH COINGECKO ================= */
        const fetchMarketCaps = async () => {
            try {
                const res = await fetch(COINGECKO_API);
                const data = await res.json();

                const map = {};
                data.forEach(c => {
                    map[c.symbol.toUpperCase()] = c.market_cap;
                });

                marketCapRef.current = map;
            } catch (e) {
                console.error("CoinGecko error", e);
            }
        };

        /* ================= SOCKET ================= */
        const connectSocket = () => {
            if (socketRef.current) socketRef.current.close();

            const ws = new WebSocket(BINANCE_WS);
            socketRef.current = ws;

            ws.onmessage = (e) => {
                if (!mountedRef.current) return;

                const data = JSON.parse(e.data);

                setTickers(prev => {
                    const map = new Map(prev.map(t => [t.symbol, t]));

                    data.forEach(t => {
                        const cgKey = toCGSymbol(t.s);

                        map.set(t.s, {
                            ...(map.get(t.s) || {}),
                            symbol: t.s,
                            lastPrice: Number(t.c),
                            livePrice: Number(t.c),
                            change24h: Number(t.P),
                            changePercent24h: Number(t.P),
                            high24h: Number(t.h),
                            low24h: Number(t.l),
                            volume24h: Number(t.v),
                            quoteVolume24h: Number(t.q),
                            marketCap:
                                marketCapRef.current[cgKey] ??
                                map.get(t.s)?.marketCap ??
                                null,
                        });
                    });

                    return Array.from(map.values());
                });
            };

            ws.onerror = () => ws.close();

            ws.onclose = () => {
                if (!mountedRef.current) return;
                reconnectRef.current = setTimeout(connectSocket, 2000);
            };
        };

        /* ================= INIT ================= */
        const init = async () => {
            setLoading(true);

            // 1️⃣ fetch market caps
            await fetchMarketCaps();

            // 2️⃣ fetch binance snapshot
            const res = await fetch(BINANCE_API);
            const data = await res.json();

            if (!mountedRef.current) return;

            setTickers(
                data.map(t => {
                    const cgKey = toCGSymbol(t.symbol);

                    return {
                        symbol: t.symbol,
                        lastPrice: Number(t.lastPrice),
                        livePrice: Number(t.lastPrice),
                        change24h: Number(t.priceChangePercent),
                        changePercent24h: Number(t.priceChangePercent),
                        high24h: Number(t.highPrice),
                        low24h: Number(t.lowPrice),
                        volume24h: Number(t.volume),
                        quoteVolume24h: Number(t.quoteVolume),
                        marketCap: marketCapRef.current[cgKey] ?? null,
                    };
                })
            );

            setLoading(false);
            connectSocket();
        };

        init();

        return () => {
            mountedRef.current = false;
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            if (socketRef.current) socketRef.current.close();
        };
    }, []);

    return { tickers, loading };
};

export default useBinanceAllTickers;
